/**
 * The phone's navigation.
 *
 * The rail has ten destinations. A tab bar cannot hold ten and should not try —
 * five is where a thumb stops being able to tell one target from the next.
 *
 * ## Which five, and why
 *
 * Chosen by what a phone is FOR, not by what the desktop happens to list first.
 * Someone on a phone is away from their desk: they are checking, not working.
 *
 *   Today    where that check starts — day P&L, open positions, rule state
 *   Journal  the reason to look at all when the session is over
 *   Chart    read-only, one symbol, what the AI currently reads
 *   Alerts   the one thing a phone is genuinely better at than a desktop
 *   More     everything else, as a list
 *
 * Analytics, Strategies, Reports, Agents, Backtest, Daily and Settings live
 * under More. Several of them say "open AEGIS on your desktop" rather than
 * rendering — a report builder on a 390pt screen is worse than an honest
 * redirect, and pretending otherwise wastes the trip.
 *
 * ## It does not hide on scroll
 *
 * A nav that disappears is a nav you have to hunt for, and this one is the only
 * way back. The pattern exists to buy vertical space on content pages; an
 * instrument is not a content page.
 *
 * @see docs/25-mobile-web-spec.md §2
 */
import type { JSX } from 'react';
import type { IconName } from '@aegis/ui';
import { Icon } from './Icon.js';
import { Link } from '../lib/router.js';

interface Tab {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
  /** Routes that should light this tab up, beyond `to` itself. */
  readonly owns?: readonly string[];
}

export const TABS: readonly Tab[] = [
  { to: '/today', label: 'Today', icon: 'today' },
  { to: '/journal', label: 'Journal', icon: 'journal' },
  { to: '/', label: 'Chart', icon: 'chart' },
  { to: '/alerts', label: 'Alerts', icon: 'alerts' },
  {
    to: '/more',
    label: 'More',
    icon: 'more',
    // More is where the other seven live, so it stays lit while you are in one
    // of them. A tab bar with nothing selected reads as broken.
    owns: [
      '/fundamentals', '/analytics', '/strategies', '/reports',
      '/agents', '/backtest', '/daily', '/settings', '/tax',
    ],
  },
];

/**
 * Exported for the test. `/` is exact — every path starts with it, and a
 * prefix match would light Chart on every screen in the app.
 */
export function activeTab(path: string): string | null {
  for (const t of TABS) {
    if (t.to === '/' ? path === '/' : path === t.to || path.startsWith(`${t.to}/`)) return t.to;
    if (t.owns?.some((p) => path === p || path.startsWith(`${p}/`))) return t.to;
  }
  return null;
}

export function MobileTabBar({ route }: { route: string }): JSX.Element {
  const active = activeTab(route);

  return (
    <nav className="tabbar" aria-label="Sections">
      {TABS.map((t) => {
        const on = active === t.to;
        return (
          <Link
            key={t.to}
            to={t.to}
            className={`tab ${on ? 'on' : ''}`}
            /* `page`, not `true` — the tab is not a pressed button, it is the
               location you are already at. */
            aria-current={on ? 'page' : undefined}
          >
            <Icon name={t.icon} />
            <span className="tab-label">{t.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
